import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { BottomSheet, Button, Dropdown, InputField } from 'components';
import { ThreeDots } from 'icons';
import { saveInvoiceDocDownload } from 'data';
import { Tooltip } from 'primereact/tooltip';
import { t } from 'i18next';
import { profileCompletedApi } from 'data/api/userApi';
import { useScreenSize } from 'hooks';

/* eslint-disable no-unused-vars */
type TableRow = Record<string, any>;

type CellVariant = 'table' | 'card';

type RowAction = {
  label: string;
  onClick: () => void;
  className?: string;
  disabled?: boolean;
};

interface CardLayout {
  title: string;
  aside?: string;
  status?: string;
  primary?: string;
  secondary?: string;
  meta?: string[];
}

interface TableProps {
  data: Array<TableRow>;
  columns: Array<{ key: string; label: string }>;
  searchField?: boolean;
  searchFunctionality?: boolean;
  actions?: (row: TableRow) => Array<RowAction>;
  primaryButton?: { label?: string; icon?: any; onClick: () => void };
  secondaryButton?: { label?: string; icon?: any; onClick: () => void };
  currentPage?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
  statusSearchDropdown?: boolean;
  cardLayout?: CardLayout;
}

const STATUS_COLORS: Record<string, string> = {
  0: '#828E9F',
  1: '#5458C1',
  2: '#54C158',
};

const ActionDropdown: React.FC<{
  actions: Array<RowAction>;
  position: { top: number; left: number };
  onClose: () => void;
}> = ({ actions, position, onClose }) => {
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  return createPortal(
    <div
      ref={dropdownRef}
      className="absolute bg-white border shadow-xl rounded z-50 w-40"
      style={{ top: position.top, left: position.left }}
    >
      {actions.map((action, i) => (
        <button
          key={i}
          disabled={action.disabled}
          onClick={(e) => {
            e.stopPropagation();
            action.onClick();
            onClose();
          }}
          className={`block px-4 py-2 text-sm w-full text-left ${
            action.disabled
              ? 'text-gray-400 cursor-not-allowed'
              : `hover:bg-gray-100 ${action.className || 'text-gray-700'}`
          }`}
        >
          {action.label}
        </button>
      ))}
    </div>,
    document.body,
  );
};

export const Table: React.FC<TableProps> = ({
  data,
  columns,
  searchField = false,
  searchFunctionality = false,
  actions,
  primaryButton,
  secondaryButton,
  currentPage,
  totalPages,
  onPageChange,
  statusSearchDropdown = false,
  cardLayout,
}) => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const buttonRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const [status, setStatus] = useState<string | null>(null);
  const [profileCompleted, setProfileCompleted] = React.useState(false);
  const { isCompact } = useScreenSize();
  const onPageChangeRef = useRef(onPageChange);
  onPageChangeRef.current = onPageChange;

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(timeout);
  }, [searchTerm]);

  useEffect(() => {
    if (debouncedSearch) onPageChangeRef.current?.(1);
  }, [debouncedSearch]);

  // Derived, not stored: `data` is a fresh array every render, so holding the
  // filtered rows in state re-triggers the filter forever.
  const filteredData = useMemo(() => {
    const term = debouncedSearch.toLowerCase();
    return data.filter((item) => {
      const matchesTerm =
        !searchFunctionality ||
        !term ||
        Object.values(item).some(
          (value) =>
            typeof value === 'string' && value.toLowerCase().includes(term),
        );
      const matchesStatus =
        !status || status === 'all' || item.status === status;
      return matchesTerm && matchesStatus;
    });
  }, [data, debouncedSearch, searchFunctionality, status]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const profileCompletedResp = await profileCompletedApi();
        setProfileCompleted(profileCompletedResp.status);
      } catch (error) {
        console.error('Error fetching profile completed status:', error);
      }
    };

    fetchData();
  }, []);

  const getStatusStyle = (statusValue: string) => {
    return `inline-block px-3 py-1 rounded-full text-white font-medium text-sm text-center ${
      {
        0: 'bg-[#828E9F]',
        1: 'bg-[#5458c1]',
        2: 'bg-[#54C158]',
      }[statusValue] || 'bg-gray-300'
    }`;
  };

  const handleOpenMenu = (rowId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (isCompact) {
      setOpenMenu(openMenu === rowId ? null : rowId);
      return;
    }
    const button = buttonRefs.current.get(rowId);
    if (button) {
      const rect = button.getBoundingClientRect();
      setDropdownPosition({
        top: Math.min(
          rect.bottom + window.scrollY,
          window.scrollY + window.innerHeight - 8,
        ),
        left: Math.max(
          8,
          Math.min(rect.right + window.scrollX - 180, window.innerWidth - 168),
        ),
      });
    }
    setOpenMenu(openMenu === rowId ? null : rowId);
  };

  const downloadInvoice = async (
    e: React.MouseEvent,
    docId: number,
    fallbackFilename: string,
  ) => {
    e.preventDefault();

    try {
      await saveInvoiceDocDownload(docId, fallbackFilename);
    } catch (error) {
      console.log('downloadInvoiceDoc.error', error);
    }
  };

  const getStatusLabel = (statusValue: string) => {
    switch (statusValue) {
      case '0':
        return t('invoices.pending');
      case '1':
        return t('invoices.sent');
      case '2':
        return t('invoices.paid');
      default:
        return '';
    }
  };

  const renderStatus = (row: TableRow, key: string) => (
    <div className="flex items-center">
      <span className={getStatusStyle(row[key])}>
        {getStatusLabel(row[key])}
      </span>
      {!profileCompleted && (
        <div>
          <Tooltip target=".complete-profile-info" />
          <i
            className="complete-profile-info pi pi-info-circle p-text-secondary p-overlay-badge ml-1"
            data-pr-tooltip={t('completeProfileInfo') || ''}
            data-pr-position="right"
            data-pr-at="right+5 top"
            data-pr-my="left center-2"
            style={{ fontSize: '1.3rem', cursor: 'pointer' }}
          ></i>
        </div>
      )}
    </div>
  );

  const renderAmountWithDoc = (
    row: TableRow,
    key: string,
    docIdKey: string,
    filenamePrefix: string,
    chipColor: string,
    variant: CellVariant,
  ) => (
    <span
      className={`flex items-center gap-[8px] ${
        variant === 'card' ? 'justify-end' : 'justify-between'
      }`}
    >
      {row[docIdKey] > 0 ? (
        <a
          href=""
          aria-label={t('table.downloadFile') as string}
          className={
            variant === 'card'
              ? 'h-11 w-11 flex items-center justify-center -my-2'
              : ''
          }
          onClick={(e) =>
            downloadInvoice(e, row[docIdKey], `${filenamePrefix}_${row.id}.pdf`)
          }
        >
          <span
            className={`inline-block px-3 py-1 rounded-full text-white font-medium text-sm text-center ${chipColor}`}
          >
            <i className="pi pi-download"></i>
          </span>
        </a>
      ) : (
        variant === 'table' && <span className="inline-block w-[34px]" />
      )}
      <span className="tnum">
        {row['currency']} {row[key]}
      </span>
    </span>
  );

  const renderCellValue = (
    key: string,
    row: TableRow,
    variant: CellVariant = 'table',
  ) => {
    if (key === 'status') return renderStatus(row, key);
    if (key === 'totalInvoice')
      return renderAmountWithDoc(
        row,
        key,
        'invoiceDocId',
        'invoice',
        row['invoiceNumber'] ? 'bg-[#50b154]' : 'bg-[#828E9F]',
        variant,
      );
    if (key === 'totalPayslip' && row[key] !== '-')
      return renderAmountWithDoc(
        row,
        key,
        'payslipDocId',
        'payslip',
        'bg-[#50b154]',
        variant,
      );
    if (key === 'date')
      return <div className="text-right tnum">{row[key]}</div>;
    return row[key];
  };

  const columnLabel = (key: string) =>
    columns.find((column) => column.key === key)?.label || key;

  const rowActions = (row: TableRow) => (actions ? actions(row) : []);

  const openRow = filteredData.find((row) => String(row.id) === openMenu);
  const openRowActions = openRow ? rowActions(openRow) : [];

  const clearSearch = () => setSearchTerm('');

  const renderActionTrigger = (row: TableRow, variant: 'table' | 'card') => {
    if (rowActions(row).length === 0) return null;
    return (
      <button
        ref={(el: any) => {
          if (el && variant === 'table') {
            buttonRefs.current.set(String(row.id), el);
          }
        }}
        aria-label={t('invoices.actions') as string}
        aria-haspopup="menu"
        className="h-11 w-11 flex items-center justify-center hover:bg-gray-100 active:bg-gray-200 rounded-full shrink-0"
        onClick={(e) => handleOpenMenu(String(row.id), e)}
      >
        <ThreeDots />
      </button>
    );
  };

  const renderCard = (row: TableRow) => {
    const layout: CardLayout = cardLayout || {
      title: columns[0]?.key,
      meta: columns.slice(1).map((column) => column.key),
    };
    const railColor = layout.status
      ? STATUS_COLORS[row[layout.status]] || '#D5D9DF'
      : null;
    const metaKeys = (layout.meta || []).filter(
      (key) => row[key] !== undefined && row[key] !== '' && row[key] !== 'N/A',
    );

    return (
      <article
        key={row.id}
        className="relative bg-white rounded-[16px] border border-[#E0E1E2] overflow-hidden"
      >
        {railColor && (
          <span
            aria-hidden="true"
            className="absolute left-0 top-0 bottom-0 w-[4px]"
            style={{ backgroundColor: railColor }}
          />
        )}
        <div
          className={`${railColor ? 'pl-[18px]' : 'pl-[16px]'} pr-[8px] py-[14px] flex flex-col gap-[8px]`}
        >
          <div className="flex items-center justify-between gap-[8px] min-h-[28px]">
            <div className="flex items-center gap-[8px] min-w-0 flex-1">
              {layout.status && renderStatus(row, layout.status)}
              {layout.aside && (
                <span className="text-[13px] text-[#828E9F] tnum shrink-0">
                  {row[layout.aside]}
                </span>
              )}
              {!layout.status && !layout.aside && (
                <p className="text-[16px] font-[600] text-[#2E435F] break-words min-w-0">
                  {row[layout.title]}
                </p>
              )}
            </div>
            {renderActionTrigger(row, 'card')}
          </div>

          {(layout.status || layout.aside) && (
            <p className="text-[16px] font-[600] text-[#2E435F] break-words pr-[8px]">
              {row[layout.title]}
            </p>
          )}

          {layout.primary && (
            <div className="flex items-center justify-between gap-[8px] pr-[8px]">
              <span className="text-[12px] uppercase tracking-[0.06em] text-[#999AA3] font-[600]">
                {columnLabel(layout.primary)}
              </span>
              <span className="text-[20px] leading-none text-[#2E435F] tnum">
                {renderCellValue(layout.primary, row, 'card')}
              </span>
            </div>
          )}

          {layout.secondary && row[layout.secondary] !== '-' && (
            <div className="flex items-center justify-between gap-[8px] pr-[8px]">
              <span className="text-[12px] uppercase tracking-[0.06em] text-[#999AA3] font-[600]">
                {columnLabel(layout.secondary)}
              </span>
              <span className="text-[15px] text-[#4F5057] tnum">
                {renderCellValue(layout.secondary, row, 'card')}
              </span>
            </div>
          )}

          {metaKeys.length > 0 && (
            <dl className="grid grid-cols-1 gap-[6px] pt-[4px] border-t border-[#EAECEF] mt-[2px] pr-[8px]">
              {metaKeys.map((key) => (
                <div
                  key={key}
                  className="flex items-baseline justify-between gap-[12px]"
                >
                  <dt className="text-[13px] text-[#828E9F] shrink-0">
                    {columnLabel(key)}
                  </dt>
                  <dd className="text-[14px] text-[#4F5057] text-right break-words min-w-0">
                    {row[key]}
                  </dd>
                </div>
              ))}
            </dl>
          )}
        </div>
      </article>
    );
  };

  return (
    <div className="flex flex-col w-full">
      <div className="flex flex-col md:flex-row md:items-center gap-[12px] md:gap-[8px]">
        {searchField && (
          <div className="sm:flex-1">
            <InputField
              type="search"
              placeholder={t('invoices.search')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              fullWidth={true}
            />
          </div>
        )}
        {statusSearchDropdown && (
          <div className="sm:w-40">
            <Dropdown
              label={''}
              name="statusFilter"
              value={status || 'all'}
              options={[
                { label: t('invoices.all'), value: 'all' },
                { label: t('invoices.pending'), value: '0' },
                { label: t('invoices.sent'), value: '1' },
                { label: t('invoices.paid'), value: '2' },
              ]}
              onChange={(e) => setStatus(e.target.value)}
              fullWidth
            />
          </div>
        )}
        {primaryButton && (
          <Button
            onClick={primaryButton.onClick}
            icon={primaryButton.icon}
            label={primaryButton.label}
            color="#2E435F"
            fullWidth
          />
        )}
        {secondaryButton && (
          <Button
            onClick={secondaryButton.onClick}
            icon={secondaryButton.icon}
            label={secondaryButton.label}
            fullWidth
          />
        )}
      </div>

      {filteredData.length === 0 ? (
        <div className="mt-4 flex flex-col items-center gap-[12px] py-[64px] px-[16px] bg-white rounded-[16px] border border-[#E0E1E2]">
          <p className="text-center text-[#828E9F]">{t('table.noResults')}</p>
          {searchTerm && (
            <Button
              label={t('table.clearSearch')}
              onClick={clearSearch}
              color="#828E9F"
            />
          )}
        </div>
      ) : (
        <>
          <div className="mt-4 flex flex-col gap-[12px] md:hidden">
            {filteredData.map(renderCard)}
          </div>

          <div className="mt-4 rounded-lg w-full overflow-x-auto hidden md:block">
            <table className="w-full text-[14px] table-auto">
              <thead className="bg-[#EAECEF]">
                <tr className="text-left">
                  {columns.map(({ key, label }) => (
                    <th
                      key={key}
                      scope="col"
                      className="px-4 py-2 border-r border-[#D5D9DF] font-[500] text-[#828E9F]"
                    >
                      {label}
                    </th>
                  ))}
                  {actions && (
                    <th
                      scope="col"
                      className="px-4 py-2 w-[100px] font-[500] text-[#828E9F] text-center"
                    >
                      {t('invoices.actions')}
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white">
                {filteredData.map((row) => (
                  <tr key={row.id}>
                    {columns.map(({ key }) => (
                      <td
                        key={key}
                        className="px-4 py-2 border-t border-r border-[#EAECEF] text-[16px]"
                      >
                        {renderCellValue(key, row)}
                      </td>
                    ))}
                    {actions && (
                      <td className="px-4 py-2 border-t w-[100px] text-center relative">
                        <div className="inline-block">
                          {renderActionTrigger(row, 'table')}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {openMenu &&
        !isCompact &&
        openRowActions.length > 0 &&
        dropdownPosition && (
          <ActionDropdown
            actions={openRowActions}
            position={dropdownPosition}
            onClose={() => setOpenMenu(null)}
          />
        )}

      <BottomSheet
        isOpen={Boolean(openMenu) && isCompact && openRowActions.length > 0}
        onClose={() => setOpenMenu(null)}
        title={
          openRow && cardLayout
            ? openRow[cardLayout.title]
            : t('invoices.actions')
        }
      >
        <ul>
          {openRowActions.map((action, index) => (
            <li key={index}>
              <button
                disabled={action.disabled}
                onClick={() => {
                  action.onClick();
                  setOpenMenu(null);
                }}
                className={`w-full text-left px-4 min-h-[52px] flex items-center rounded-[12px] text-[16px] ${
                  action.disabled
                    ? 'text-gray-400 cursor-not-allowed'
                    : `active:bg-gray-100 ${
                        action.className?.includes('text-red')
                          ? 'text-red-600'
                          : 'text-[#2E435F]'
                      }`
                }`}
              >
                {action.label}
              </button>
            </li>
          ))}
        </ul>
      </BottomSheet>

      {totalPages !== undefined &&
        currentPage !== undefined &&
        onPageChange &&
        totalPages > 1 && (
          <div className="flex justify-between items-center mt-4 gap-[8px] w-full">
            <Button
              label={t('invoices.previous')}
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 1}
            />
            <span className="text-[#828E9F] text-[13px] sm:text-sm tnum text-center">
              {t('invoices.page')} {currentPage} {t('invoices.of')} {totalPages}
            </span>
            <Button
              label={t('invoices.next')}
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            />
          </div>
        )}
    </div>
  );
};
